import {
	ChangeDetectionStrategy,
	Component,
	inject,
	signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogBase, DialogOptions } from '@context/frontend/common';
import { applyValidators } from '@context/frontend/form';
import { getFirebaseErrorKey } from '@context/shared/utils';
import { TranslocoModule } from '@jsverse/transloco';
import {
	TuiButton,
	TuiError,
	TuiIcon,
	TuiInput,
	TuiLabel,
	TuiNotification,
} from '@taiga-ui/core';
import { TuiButtonLoading, TuiPassword } from '@taiga-ui/kit';
import { FirebaseError } from 'firebase/app';
import { AuthService } from '../../services';

export type SignInDialogData = {
	email: string;
	inviteId: string;
};

@Component({
	imports: [
		TranslocoModule,
		TuiButton,
		ReactiveFormsModule,
		TuiInput,
		TuiLabel,
		TuiButtonLoading,
		TuiIcon,
		TuiPassword,
		TuiError,
		TuiNotification,
	],
	selector: 'ctx-sign-in-dialog',
	templateUrl: 'sign-in-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignInDialogComponent extends applyValidators(
	DialogBase<boolean, SignInDialogData>,
) {
	static override readonly DialogOptions = {
		dismissible: false,
		closable: false,
		label: 'auth.sign-in',
		size: 's',
	} as DialogOptions;

	readonly busy = signal(false);
	readonly error = signal<string | null>(null);

	form: FormGroup | null = null;

	private readonly authService = inject(AuthService);

	constructor() {
		super();
		this.setupForm();
	}

	setupForm() {
		const email = this.context.data?.email ?? '';
		this.form = new FormGroup({
			email: new FormControl({ value: email, disabled: true }),
			password: new FormControl(null, (field) =>
				this.getRequiredValidator(
					field,
					'form.error.password.required',
				),
			),
		});
	}

	onSubmit() {
		this.form?.markAllAsTouched();
		if (this.busy() || !this.form?.valid) return;

		this.form.disable();
		this.busy.set(true);
		this.error.set(null);

		const { email, password } = this.form.getRawValue();
		this.authService
			.signInWithEmailAndPassword(email, password)
			.then(() => this.onDismiss(true))
			.catch((err: FirebaseError) => {
				this.error.set(getFirebaseErrorKey(err.code));
				this.busy.set(false);
				this.form?.enable();
			});
	}

	onCancel() {
		this.onDismiss(false);
	}
}
